import {
  CASINO_RULES,
  CHANCE_GAMES_OFF_MESSAGE,
  type BlackjackSeatState,
  type BlackjackTableState,
  type CasinoCard,
  type CasinoPrivate,
  type CasinoState,
} from "@townsy/contracts";
import { cardLabel, cardSpoken, chipsText, isRedSuit } from "./casinoView";

/** Shared pieces of the casino overlay (compact panels and the big table / slot views). */
export const BJ = CASINO_RULES.blackjack;
export const BET_STEPS = [1, 5, 10, 25, 50] as const;

export interface Ctx {
  state: CasinoState;
  mine: CasinoPrivate;
  chips: number;
  betting: boolean;
  /**
   * A guest while betting is on (ADR-0048: betting needs an account): the panels offer "Sign up (18+) to play" instead
   * of the betting controls. With betting switched off for everyone, the controls stay, disabled, saying why.
   */
  signUp: boolean;
  send(action: Record<string, unknown>): boolean;
  /** Seconds left of a countdown in the shared state. */
  left(endsIn: number | null): number | null;
  /** Seconds left of a countdown in the visitor's own state. */
  leftMine(ms: number): number;
  localActorId: string | null;
  /** Wall clock of this render (ms). */
  now: number;
}

/** Why a betting control is off, as its tooltip. */
export const offTitle = (betting: boolean, otherwise?: string) => (!betting ? CHANCE_GAMES_OFF_MESSAGE : otherwise);

/** Cards as small text chips (compact panel). */
export function Cards({ cards, label }: { cards: CasinoCard[]; label: string }) {
  if (cards.length === 0) return <span className="casino-muted">no cards</span>;
  return (
    <span className="casino-cards" role="img" aria-label={`${label}: ${cards.map(cardSpoken).join(", ")}`}>
      {cards.map((c, i) => (
        <span key={i} className={`casino-card${isRedSuit(c) ? " red" : ""}${c === "XX" ? " back" : ""}`} aria-hidden="true">
          {cardLabel(c)}
        </span>
      ))}
    </span>
  );
}

/** Card graphics (big table view): rank over suit, red or black; the hole card face down. */
export function CardFaces({ cards, label, size = "md" }: { cards: CasinoCard[]; label: string; size?: "lg" | "md" }) {
  return (
    <span className={`casino-cardrow ${size}`} role="img" aria-label={cards.length ? `${label}: ${cards.map(cardSpoken).join(", ")}` : `${label}: no cards`}>
      {cards.map((c, i) => {
        if (c === "XX") return <span key={i} className="casino-cardface back" aria-hidden="true" />;
        const text = cardLabel(c);
        return (
          <span key={i} className={`casino-cardface${isRedSuit(c) ? " red" : ""}`} aria-hidden="true">
            <span className="r">{text.slice(0, -1)}</span>
            <span className="s">{text.slice(-1)}</span>
          </span>
        );
      })}
    </span>
  );
}

export function handTotal(s: { total: number; soft: boolean; cards: CasinoCard[] }): string {
  if (s.cards.length === 0) return "";
  return s.soft && s.total < 21 ? `soft ${s.total}` : String(s.total);
}

export const OUTCOME_TEXT: Record<NonNullable<BlackjackSeatState["outcome"]>, string> = {
  blackjack: "Blackjack",
  win: "Win",
  push: "Push",
  lose: "Lose",
  bust: "Bust",
};

export function phaseText(t: BlackjackTableState, left: number | null, mySeat: number | null): string {
  switch (t.phase) {
    case "idle":
      return "Waiting for a bet";
    case "betting":
      return `Betting closes in ${left ?? 0} s`;
    case "playing":
      return t.turn === mySeat ? `Your turn: ${left ?? 0} s` : `Seat ${(t.turn ?? 0) + 1} to play (${left ?? 0} s)`;
    case "dealer":
      return "The dealer plays";
    case "result":
      return "Round over";
  }
}

export function ChipPicker<T extends number>({
  label,
  values,
  value,
  max,
  onPick,
  disabled,
}: {
  label: string;
  values: readonly T[];
  value: number;
  max: number;
  onPick(v: T): void;
  disabled?: boolean;
}) {
  return (
    <span className="casino-picker" role="group" aria-label={`${label} (play chips)`}>
      <span className="casino-picker-label">{label}</span>
      {values.map((v) => (
        <button key={v} type="button" className="btn btn-sm casino-chip-btn" aria-pressed={value === v} disabled={disabled || v > max} onClick={() => onPick(v)}>
          {v}
        </button>
      ))}
    </span>
  );
}

/**
 * The seated player's controls (compact panel and table view): bet + Deal me in before a round, Hit / Stand / Double on
 * your turn, Leave seat.
 */
export function SeatControls({ ctx, table, amount, setAmount, big = false }: { ctx: Ctx; table: number; amount: number; setAmount(v: number): void; big?: boolean }) {
  const t = ctx.state.tables[table];
  const seat = ctx.mine.seat?.table === table ? ctx.mine.seat.seat : null;
  const me = t && seat !== null ? (t.seats[seat] ?? null) : null;
  if (!t || !me || seat === null) return null;
  const canBet = me.bet === 0 && (t.phase === "idle" || t.phase === "betting");
  const myTurn = t.phase === "playing" && t.turn === seat;
  const canDouble = myTurn && me.cards.length === 2 && !me.doubled && ctx.chips >= me.bet;
  const stake = Math.min(amount, BJ.maxBet);
  const size = big ? "btn" : "btn btn-sm";
  return (
    <>
      {canBet ? (
        <div className="casino-actions">
          <ChipPicker label="Bet" values={BET_STEPS} value={stake} max={Math.min(ctx.chips, BJ.maxBet)} onPick={setAmount} disabled={!ctx.betting} />
          <button
            type="button"
            className={`${size} btn-primary`}
            disabled={!ctx.betting || ctx.chips < stake}
            title={offTitle(ctx.betting, ctx.chips < stake ? `You have ${chipsText(ctx.chips)}.` : undefined)}
            onClick={() => ctx.send({ type: "bj.bet", amount: stake })}
            data-testid="casino-deal-in"
          >
            Deal me in · {stake}
          </button>
        </div>
      ) : null}
      {myTurn ? (
        <div className="casino-actions" role="group" aria-label="Your move">
          <button type="button" className={`${size} btn-primary`} onClick={() => ctx.send({ type: "bj.hit" })} data-testid="casino-hit">
            Hit
          </button>
          <button type="button" className={size} onClick={() => ctx.send({ type: "bj.stand" })} data-testid="casino-stand">
            Stand
          </button>
          <button
            type="button"
            className={size}
            disabled={!canDouble}
            title={me.cards.length !== 2 ? "Only on your first two cards." : ctx.chips < me.bet ? `Doubling needs ${chipsText(me.bet)} more.` : `Add ${me.bet} and take one last card.`}
            onClick={() => ctx.send({ type: "bj.double" })}
            data-testid="casino-double"
          >
            Double · {me.bet}
          </button>
        </div>
      ) : null}
      <div className="casino-actions">
        <button type="button" className="btn btn-ghost btn-sm" onClick={() => ctx.send({ type: "bj.leave" })} data-testid="casino-leave-seat">
          Leave seat
        </button>
        <span className="small casino-muted">
          {me.bet > 0 && me.cards.length > 0 && !me.outcome ? "Leaving now loses this hand's bet." : "A bet placed before the deal comes back if you leave."}
        </span>
      </div>
    </>
  );
}
